// Rhythm Lab source: core's `labLoop` rendered at the context's sample rate, ready for the transport.
//
// The stretcher reads a window of audio around the playhead (about 120 ms), and at a loop wrap that window
// reaches back before the loop start. With one copy of the loop that is silence, and the first beat after each
// wrap comes back at a third of its level (measured, docs/design/spikes.md). So the buffer is three copies of
// the loop and the stretcher loops the middle one: the audio on both sides of every wrap is the loop's own.
// Song time in the lab is one lap, [0, loopSeconds); the transport adds `inputOffset` to reach the middle copy.

import { DEFAULT_BELL, labLoop, type BellVoice, type ClaveDirection, type CountMap, type LabInstrument } from '@salsacount/core';

export interface LabSettings {
  /** 140 to 220. */
  readonly bpm: number;
  readonly clave: ClaveDirection;
  readonly on: Readonly<Record<LabInstrument, boolean>>;
  /** The cowbell's voice: its tone, not its timing, so changing it moves no beat and no count. */
  readonly bell: BellVoice;
}

export const DEFAULT_LAB: LabSettings = { bpm: 170, clave: '3-2', on: { clave: true, conga: true, bass: true, bell: true }, bell: DEFAULT_BELL };

/**
 * Phrases in one lap. 16, so that every Test me pattern (2, 4 or 8 phrases shown, as many hidden: a cycle of 4, 8 or 16)
 * fits a whole number of times into a lap and the hidden stretches stay in step across the wrap.
 */
export const LAB_PHRASES = 16;

export interface LabSource {
  /** Mono: three copies of the loop. The stretcher spreads it over both ears. */
  readonly channels: Float32Array[];
  readonly sampleRate: number;
  /** One lap, seconds: exactly LAB_PHRASES * 8 * 60 / bpm. */
  readonly duration: number;
  /** Seconds to add to song time to reach the same sound in the middle copy. */
  readonly inputOffset: number;
  /** The lab's exact map: first beat at 0, counts run 1..8 from it. */
  readonly map: CountMap;
}

export function renderLab(settings: LabSettings, sampleRate: number): LabSource {
  const loop = labLoop({ bpm: settings.bpm, phrases: LAB_PHRASES, clave: settings.clave, on: settings.on, bell: settings.bell, sampleRate });
  const n = loop.samples.length;
  const three = new Float32Array(3 * n);
  for (let k = 0; k < 3; k++) three.set(loop.samples, k * n);
  const duration = loop.loopSeconds;
  return { channels: [three], sampleRate, duration, inputOffset: n / sampleRate, map: { origin: 0, beat: 60 / settings.bpm } };
}
